// Angular
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
// RxJS
import { Subject } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';
// Translate
import { TranslateService } from '@ngx-translate/core';
// Store
import { Store } from '@ngrx/store';
import { AppState } from '../../../../core/reducers';
// Auth
import { AuthNoticeService, AuthService, isLoggedIn } from '../../../../core/auth';

@Component({
  selector: 'kt-login',
  templateUrl: './login.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class LoginComponent implements OnInit, OnDestroy {
  submitted = false;
  // Public params
  loginForm: FormGroup;
  loading = false;
  errors: any = [];
  hide: any;
  validEmail = false;
  validUsername = false;
  inputType: string;
  private unsubscribe: Subject<any>;
  returnUrl: any;
  workOSExist = true;
  firstSubmit = true;
  enterEmail = true;
  @ViewChild('passwordField', { static: false }) passwordField: ElementRef;

  /**
   * Component constructor
   *
   * @param router: Router
   * @param auth: AuthService
   * @param authNoticeService: AuthNoticeService
   * @param translate: TranslateService
   * @param store: Store<AppState>
   * @param fb: FormBuilder
   * @param cdr: ChangeDetectorRef
   * @param route: ActivatedRoute
   */
  constructor(
    private router: Router,
    private auth: AuthService,
    private authNoticeService: AuthNoticeService,
    private translate: TranslateService,
    private store: Store<AppState>,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
  ) {
    this.unsubscribe = new Subject();
    // redirect to home if already logged in
    this.store.select(isLoggedIn).subscribe((res) => {
      if (res) {
        this.router.navigate(['/']);
      }
    });
  }

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit(): void {
    this.initLoginForm();
    this.route.queryParams.subscribe((params) => {
      this.returnUrl = params.returnUrl || '/';
    });

    if (this.isLocalOrPreviewEnvironment()) {
      this.workOSExist = false;
    }
  }

  /**
   * Check if running in local development or preview environment
   * @returns true if WorkOS SSO should be bypassed
   */
  private isLocalOrPreviewEnvironment(): boolean {
    const hostname = window.location.hostname;
    return (
      hostname === 'localhost' ||
      hostname.includes('preview.knostos.com') ||
      hostname.includes('preview-') ||
      hostname.includes('.preview.')
    );
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this.authNoticeService.setNotice(null);
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.loading = false;
  }

  /**
   * Form initalization
   * Default params, validators
   */
  initLoginForm() {
    this.loginForm = this.fb.group({
      email: new FormControl(
        '',
        Validators.compose([Validators.minLength(4), Validators.maxLength(320)]),
      ),
      password: new FormControl(''),
    });
  }

  /**
   * Form Submit
   */
  submit() {
    const controls = this.loginForm.controls;
    if (controls.email.value === '') {
      controls.email.setErrors({ required: true });
      return;
    }
    if (this.firstSubmit && !this.isLocalOrPreviewEnvironment()) {
      this.checkSso();
      return;
    }
    if (controls.password.value === '' && !this.firstSubmit) {
      controls.password.setErrors({ required: true });
      return;
    }
    this.loading = true;
    const authData = {
      email: controls.email.value,
      password: controls.password.value,
    };
    this.auth
      .login(authData.email, authData.password)
      .pipe(
        tap((user) => {
          if (user) {
            this.router.navigateByUrl(this.returnUrl);
          } else {
            this.authNoticeService.setNotice(
              this.translate.instant('AUTH.VALIDATION.INVALID_LOGIN'),
              'danger',
            );
          }
        }),
        takeUntil(this.unsubscribe),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe();
  }

  /**
   * Checking control validation
   *
   * @param controlName: string => Equals to formControlName
   * @param validationType: string => Equals to valitors name
   */
  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.loginForm.controls[controlName];
    if (!control) {
      return false;
    }
    return control.hasError(validationType) && (control.dirty || control.touched);
  }
  checkSso() {
    this.auth.checkSSO(this.loginForm.controls.email.value).subscribe(
      (res: any) => {
        if (res && res.auth_url) {
          location.replace(res.auth_url);
        }
      },
      () => {
        this.workOSExist = false;
        this.firstSubmit = false;
        this.enterEmail = false;
        setTimeout(() => {
          this.passwordField.nativeElement.focus();
        }, 0);
        this.cdr.detectChanges();
      },
    );
  }
  changeEmail() {
    if (this.isLocalOrPreviewEnvironment()) {
      this.workOSExist = false;
    } else {
      this.workOSExist = true;
    }
    this.firstSubmit = true;
    this.enterEmail = true;
  }
}
